import { track } from '../../../services/analytics/analytics'

// Types for tracking events
interface BaseEventProperties {
  timestamp?: string
  session_id?: string
  [key: string]: string | string[] | number | boolean | undefined | null
}

interface RightActionBarInstallBotOpenedProperties extends BaseEventProperties {
  user_role: string
  time_since_page_load_ms: number
}

interface RightActionBarHelpdeskOpenedProperties extends BaseEventProperties {
  time_since_page_load_ms: number
}

interface RightActionBarDocsOpenedProperties extends BaseEventProperties {
  page_url?: string
  article_id?: string
  article_title?: string
  app_page_url: string
  time_since_page_load_ms: number
}

interface RightActionBarDocsHomeClickedProperties extends BaseEventProperties {
  current_page_url?: string
  time_since_page_load_ms: number
}

interface RightActionBarDocsOpenInNewTabClickedProperties extends BaseEventProperties {
  page_url: string
  article_id?: string
  time_since_page_load_ms: number
}

interface RightActionBarLatestFeaturesOpenedProperties extends BaseEventProperties {
  unread_count: number
  time_since_page_load_ms: number
}

interface RightActionBarChangelogClickedProperties extends BaseEventProperties {
  time_since_page_load_ms: number
  source: 'whats_new_drawer' | 'direct_link'
}

interface RightActionBarOnboardingOpenedProperties extends BaseEventProperties {
  onboarding_type: 'admin' | 'user' | 'approver'
  current_step: number
  total_steps: number
  time_since_page_load_ms: number
}

// Session tracking state
class RightActionBarSessionTracker {
  private sessionStartTime: number = Date.now()
  private sessionId = `right_action_bar_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private pageLoadTime: number = Date.now()

  // Public tracking methods
  trackInstallBotOpened(userData: {
    userRole: string
  }): void {
    const properties: RightActionBarInstallBotOpenedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      user_role: userData.userRole,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_INSTALL_BOT_OPENED', properties)
  }

  trackHelpdeskOpened(): void {
    const properties: RightActionBarHelpdeskOpenedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_HELPDESK_OPENED', properties)
  }

  trackDocsOpened(docsData: {
    pageUrl?: string
    articleId?: string
    articleTitle?: string
    appPageUrl: string
  }): void {
    const properties: RightActionBarDocsOpenedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      page_url: docsData.pageUrl,
      article_id: docsData.articleId,
      article_title: docsData.articleTitle,
      app_page_url: docsData.appPageUrl,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_DOCS_OPENED', properties)
  }

  trackDocsHomeClicked(homeData: {
    currentPageUrl?: string
  }): void {
    const properties: RightActionBarDocsHomeClickedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      current_page_url: homeData.currentPageUrl,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_DOCS_HOME_CLICKED', properties)
  }

  trackDocsOpenInNewTabClicked(newTabData: {
    pageUrl: string
    articleId?: string
  }): void {
    const properties: RightActionBarDocsOpenInNewTabClickedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      page_url: newTabData.pageUrl,
      article_id: newTabData.articleId,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_DOCS_OPEN_IN_NEW_TAB_CLICKED', properties)
  }

  trackLatestFeaturesOpened(featuresData: {
    unreadCount: number
  }): void {
    const properties: RightActionBarLatestFeaturesOpenedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      unread_count: featuresData.unreadCount,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_LATEST_FEATURES_OPENED', properties)
  }

  trackChangelogClicked(changelogData: {
    source: 'whats_new_drawer' | 'direct_link'
  }): void {
    const properties: RightActionBarChangelogClickedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      source: changelogData.source,
    }

    track('RIGHT_ACTION_BAR_CHANGELOG_CLICKED', properties)
  }

  trackOnboardingOpened(onboardingData: {
    onboardingType: 'admin' | 'user' | 'approver'
    currentStep: number
    totalSteps: number
  }): void {
    const properties: RightActionBarOnboardingOpenedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      onboarding_type: onboardingData.onboardingType,
      current_step: onboardingData.currentStep,
      total_steps: onboardingData.totalSteps,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('RIGHT_ACTION_BAR_ONBOARDING_OPENED', properties)
  }

  // Initialize tracking on page load
  init(): void {
    this.pageLoadTime = Date.now()
  }
}

// Create and export singleton instance
export const rightActionBarTracker = new RightActionBarSessionTracker()

